import { formatReceiptMoney, formatReceiptWhen, escapeReceiptHtml } from './format';
import { encodeEscPosChars, ESC_POS_CUT, ESC_POS_PROLOGUE } from './escposCharset';
import { escposQrBlock } from './escposQr';
import { thermalItemRowsHtml, wrapThermalHtml } from './renderThermalHtml';
import type { SaleReceiptDocument, SaleReceiptPayload } from './types';

function saleTrackingUrl(payload: SaleReceiptPayload): string | null {
  const url = payload.trackingUrl?.trim();
  return url || null;
}

function saleTrackingQrHtml(url: string, caption: string): string {
  const enc = encodeURIComponent(url);
  return `<div class="center"><img src="https://api.qrserver.com/v1/create-qr-code/?size=160x160&amp;data=${enc}" alt="" style="display:block;width:40mm;max-width:100%;margin:8px auto;"/></div><div class="meta center">${escapeReceiptHtml(caption)}</div>`;
}

function encoder(): { bytes: number[]; push: (...vals: number[]) => void; text: (s: string) => void } {
  const bytes: number[] = [];
  return {
    bytes,
    push(...vals) {
      bytes.push(...vals);
    },
    text(s: string) {
      bytes.push(...encodeEscPosChars(s));
    },
  };
}

function receiptPayments(payload: SaleReceiptPayload) {
  return payload.payments.filter((p) => p.amount > 0);
}

function receiptRefLine(payload: SaleReceiptPayload): string {
  const orderRef = payload.orderRefLabel?.trim();
  if (orderRef) return orderRef;
  return payload.saleRef ? `Ref. ${payload.saleRef}` : 'Ref. —';
}

function receiptFooter(payload: SaleReceiptPayload): string {
  return payload.footerMessage?.trim() || 'Gracias por tu compra';
}

/** Nombre en mostrador: «Retiro» solo para llevar; comer acá usa «A nombre de». */
function guestNameLine(payload: SaleReceiptPayload): string | null {
  const name = payload.pickupName?.trim();
  if (!name) return null;
  if (payload.fulfillmentLabel?.trim() === 'Comer acá') {
    return `A nombre de: ${name}`;
  }
  return `Retiro: ${name}`;
}

/** Pie bajo el QR — el encabezado ya trae Pedido #N. */
function saleTrackingQrLabel(payload: SaleReceiptPayload): string {
  if (payload.fulfillmentLabel?.trim() === 'Comer acá') {
    return 'Escaneá el QR — te avisamos cuando esté listo.';
  }
  return 'Escaneá el QR — seguí tu pedido acá.';
}

/** Con QR no repetimos Pedido #N ni otro aviso de listo en el pie. */
function receiptClosingMessage(payload: SaleReceiptPayload): string | null {
  if (saleTrackingUrl(payload)) return null;
  return receiptFooter(payload);
}

/** ESC/POS básico para ticket de venta (CP850; acentos/ñ mapeados). */
export function renderSaleReceiptEscPos(payload: SaleReceiptPayload): Uint8Array {
  const out = encoder();
  out.push(...ESC_POS_PROLOGUE);
  out.text('TICKET DE VENTA\n');
  if (payload.voided) out.text('*** ANULADA ***\n');
  out.text(`${payload.branchName}\n`);
  if (payload.organizationName?.trim()) out.text(`${payload.organizationName.trim()}\n`);
  out.text(`${formatReceiptWhen(payload.createdAt)}\n`);
  out.text(`${receiptRefLine(payload)}\n`);
  if (payload.fulfillmentLabel?.trim()) out.text(`${payload.fulfillmentLabel.trim()}\n`);
  const guestLine = guestNameLine(payload);
  if (guestLine) out.text(`${guestLine}\n`);
  if (payload.customerName?.trim()) out.text(`Cliente: ${payload.customerName.trim()}\n`);
  if (payload.diningTableName?.trim()) out.text(`Mesa: ${payload.diningTableName.trim()}\n`);
  out.text('----------------\n');
  for (const line of payload.lines) {
    out.text(`${line.qty}x ${line.name} ${formatReceiptMoney(line.lineTotal)}\n`);
    if (line.optionsLabel?.trim()) out.text(`  ${line.optionsLabel.trim()}\n`);
    if (line.lineNote?.trim()) out.text(`  Nota: ${line.lineNote.trim()}\n`);
  }
  out.text('----------------\n');
  out.text(`Subtotal ${formatReceiptMoney(payload.subtotal)}\n`);
  const discount = Math.max(0, payload.discountTotal ?? 0);
  if (discount > 0) out.text(`Descuento -${formatReceiptMoney(discount)}\n`);
  const tip = Math.max(0, Number(payload.tipAmount ?? 0) || 0);
  if (tip > 0) out.text(`Propina ${formatReceiptMoney(tip)}\n`);
  out.text(`TOTAL ${formatReceiptMoney(payload.total)}\n`);
  for (const p of receiptPayments(payload)) {
    out.text(`${p.methodLabel} ${formatReceiptMoney(p.amount)}\n`);
  }
  if (payload.changeAmount != null && payload.changeAmount > 0) {
    out.text(`Vuelto ${formatReceiptMoney(payload.changeAmount)}\n`);
  }
  const tracking = saleTrackingUrl(payload);
  if (tracking) {
    out.push(...escposQrBlock(tracking));
    out.text(`${saleTrackingQrLabel(payload)}\n`);
  }
  const closing = receiptClosingMessage(payload);
  if (closing) out.text(`${closing}\n`);
  for (const note of payload.footerNotes ?? []) {
    if (note?.trim()) out.text(`${note.trim()}\n`);
  }
  out.push(...ESC_POS_CUT);
  return Uint8Array.from(out.bytes);
}

export function buildSaleReceipt(payload: SaleReceiptPayload): SaleReceiptDocument {
  const widthMm = payload.widthMm ?? 72;
  const sep = '----------------';
  const when = formatReceiptWhen(payload.createdAt);
  const discount = Math.max(0, payload.discountTotal ?? 0);
  const tip = Math.max(0, Number(payload.tipAmount ?? 0) || 0);
  const title = payload.voided ? 'TICKET (ANULADA)' : 'TICKET DE VENTA';

  const headerLines: (string | null)[] = [
    payload.organizationName?.trim() || null,
    payload.branchName,
    when ? when : null,
    receiptRefLine(payload),
    payload.fulfillmentLabel?.trim() || null,
    guestNameLine(payload),
    payload.customerName ? `Cliente: ${payload.customerName}` : null,
    payload.diningTableName ? `Mesa: ${payload.diningTableName}` : null,
  ];

  const textLines: (string | null)[] = [
    '   TICKET DE VENTA',
    payload.voided ? '*** ANULADA ***' : null,
    ...headerLines,
    sep,
    ...payload.lines.flatMap((line) => {
      const left = `${line.qty}× ${line.name}`;
      const right = formatReceiptMoney(line.lineTotal);
      const pad = Math.max(1, 28 - left.length - right.length);
      const opts = line.optionsLabel?.trim() || '';
      const note = line.lineNote?.trim() || '';
      return [
        `${left}${' '.repeat(pad)}${right}`,
        opts ? `  ${opts}` : null,
        note ? `  Nota: ${note}` : null,
      ];
    }),
    sep,
    `Subtotal ${formatReceiptMoney(payload.subtotal)}`,
    discount > 0 ? `Descuento −${formatReceiptMoney(discount)}` : null,
    tip > 0 ? `Propina ${formatReceiptMoney(tip)}` : null,
    `Total ${formatReceiptMoney(payload.total)}`,
    ...receiptPayments(payload).map((p) => `${p.methodLabel} ${formatReceiptMoney(p.amount)}`),
    payload.changeAmount != null && payload.changeAmount > 0
      ? `Vuelto ${formatReceiptMoney(payload.changeAmount)}`
      : null,
    saleTrackingUrl(payload) ? saleTrackingQrLabel(payload) : null,
    receiptClosingMessage(payload),
    ...(payload.footerNotes ?? []).map((n) => n?.trim() || null),
  ];

  const text = textLines.filter((l): l is string => l != null && l !== '').join('\n');

  const headerHtml = headerLines
    .filter((l): l is string => Boolean(l?.trim()))
    .map((l) => `<div class="meta">${escapeReceiptHtml(l)}</div>`)
    .join('');

  const paymentRows = receiptPayments(payload)
    .map((p) => `<div class="row">${escapeReceiptHtml(p.methodLabel)} ${formatReceiptMoney(p.amount)}</div>`)
    .join('');

  const body = `
  <div class="title">${escapeReceiptHtml(title)}</div>
  ${payload.voided ? '<div class="void-banner">ANULADA</div>' : ''}
  ${headerHtml}
  <hr class="sep"/>
  <table>${thermalItemRowsHtml(payload.lines) || '<tr><td>(sin ítems)</td></tr>'}</table>
  <hr class="sep"/>
  <div class="row">Subtotal ${formatReceiptMoney(payload.subtotal)}</div>
  ${discount > 0 ? `<div class="row">Descuento −${formatReceiptMoney(discount)}</div>` : ''}
  ${tip > 0 ? `<div class="row">Propina ${formatReceiptMoney(tip)}</div>` : ''}
  <div class="total">Total ${formatReceiptMoney(payload.total)}</div>
  ${paymentRows}
  ${
    payload.changeAmount != null && payload.changeAmount > 0
      ? `<div class="row">Vuelto ${formatReceiptMoney(payload.changeAmount)}</div>`
      : ''
  }
  ${
    saleTrackingUrl(payload)
      ? saleTrackingQrHtml(saleTrackingUrl(payload)!, saleTrackingQrLabel(payload))
      : ''
  }
  ${
    receiptClosingMessage(payload)
      ? `<div class="foot">${escapeReceiptHtml(receiptClosingMessage(payload)!)}</div>`
      : ''
  }
  ${(payload.footerNotes ?? [])
    .filter((n) => n?.trim())
    .map((n) => `<div class="foot">${escapeReceiptHtml(n.trim())}</div>`)
    .join('')}`;

  return {
    text,
    html: wrapThermalHtml(body, widthMm),
    escpos: renderSaleReceiptEscPos(payload),
  };
}
